import React, { useCallback, useEffect, useRef, useState } from 'react';
import { uploadAsset, listProductAssets, setProductAssets, assetUrl } from '../../services/pimApi';
import MediaLibrary from './MediaLibrary';
import ImageLightbox from './ImageLightbox';

/*
 * Single-image or carousel media manager. Persists directly via the assets
 * endpoint (outside the batch value save) and mirrors the resulting asset
 * id(s) back into the parent value via onChange so completeness reflects it.
 */
export default function MediaManager({ sku, attribute, scope, disabled, onChange }) {
  const single = attribute.type === 'image';
  const [assets, setAssets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [libOpen, setLibOpen] = useState(false);
  const [viewing, setViewing] = useState(null); // asset being viewed full-size
  const fileRef = useRef(null);

  const channelId = scope?.channel_id ?? 0;
  const localeId = scope?.locale_id ?? 0;

  const refresh = useCallback(async () => {
    if (!sku) return;
    try {
      const rows = await listProductAssets(sku, attribute.code);
      const scoped = rows.filter((r) => r.channel_id === channelId && r.locale_id === localeId);
      setAssets(scoped.sort((a, b) => a.position - b.position));
    } catch (e) {
      setError('Failed to load media');
    }
  }, [sku, attribute.code, channelId, localeId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const persist = async (list) => {
    setBusy(true);
    setError(null);
    try {
      const ids = list.map((a) => a.asset_id);
      await setProductAssets(sku, attribute.code, ids, { channel_id: channelId, locale_id: localeId });
      setAssets(list);
      onChange(single ? (ids[0] ? { asset_id: ids[0] } : null) : { asset_ids: ids });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    setError(null);
    try {
      const uploaded = [];
      for (const f of files) {
        const a = await uploadAsset(f);
        uploaded.push({ asset_id: a.id, code: a.code, mime: a.mime, original_filename: a.original_filename });
        if (single) break;
      }
      const next = single ? uploaded.slice(0, 1) : [...assets, ...uploaded];
      await persist(next);
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onLibrarySelect = async (chosen) => {
    setLibOpen(false);
    if (!chosen.length) return;
    if (single) {
      await persist(chosen.slice(0, 1));
      return;
    }
    // Merge, de-duping by asset_id: the junction's unique constraint rejects the
    // same asset twice in a scope, so re-picking an attached image is a no-op.
    const existing = new Set(assets.map((a) => a.asset_id));
    const additions = chosen.filter((a) => !existing.has(a.asset_id));
    if (!additions.length) return;
    await persist([...assets, ...additions]);
  };

  const remove = (assetId) => persist(assets.filter((a) => a.asset_id !== assetId));

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= assets.length) return;
    const next = assets.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    persist(next);
  };

  return (
    <div className="pim-media">
      <div className="pim-media-grid">
        {assets.map((a, idx) => (
          <div key={a.asset_id} className="pim-media-tile">
            <img
              src={assetUrl(a.asset_id)}
              alt={a.original_filename || a.code}
              className="pim-media-clickable"
              title="Click to view full size"
              onClick={() => setViewing(a)}
            />
            {(a.tags || []).includes('ai-generated') && (
              <span
                className="pim-media-ai-badge"
                title={
                  (a.tags || []).find((t) => t.startsWith('source:'))
                    ? `AI-generated (reference: ${(a.tags || []).find((t) => t.startsWith('source:')).slice(7)})`
                    : 'AI-generated image'
                }
              >
                AI
              </span>
            )}
            {!disabled && (
              <div className="pim-media-actions">
                {!single && (
                  <>
                    <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0}>←</button>
                    <button type="button" onClick={() => move(idx, 1)} disabled={idx === assets.length - 1}>→</button>
                  </>
                )}
                <button type="button" className="danger" onClick={() => remove(a.asset_id)}>✕</button>
              </div>
            )}
          </div>
        ))}
        {!disabled && (single ? assets.length === 0 : true) && (
          <>
            <button type="button" className="pim-media-add" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()}>
              {busy ? 'Uploading…' : '+ Upload'}
            </button>
            <button type="button" className="pim-media-add" disabled={busy} onClick={() => setLibOpen(true)}>
              Choose from library
            </button>
          </>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple={!single}
        style={{ display: 'none' }}
        onChange={onPick}
      />
      {error && <div className="pim-error">{error}</div>}
      {libOpen && (
        <MediaLibrary
          single={single}
          attachedIds={assets.map((a) => a.asset_id)}
          onClose={() => setLibOpen(false)}
          onSelect={onLibrarySelect}
        />
      )}
      {viewing && (
        <ImageLightbox
          src={assetUrl(viewing.asset_id)}
          alt={viewing.original_filename || viewing.code}
          caption={viewing.original_filename || viewing.code}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}
